import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/lib/testIds";
import { DownloadSimple, Funnel } from "@phosphor-icons/react";

// Streams an authenticated export through axios so the Bearer/cookie travels
// with the request; then triggers a browser download of the returned blob.
async function downloadExport(url, fallbackName) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename=([^;]+)/i.exec(cd);
    const name = (m ? m[1].replace(/^["']|["']$/g, "").trim() : fallbackName) || fallbackName;
    const blob = new Blob([res.data]);
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(href); a.remove(); }, 0);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

const UNASSIGNED = "__unassigned__";

export default function Reorder() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("vendor");
  const [cat, setCat] = useState("");
  const [vendor, setVendor] = useState(""); // "" = all vendors, UNASSIGNED = blank only
  const [vendorsList, setVendorsList] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);

  // Distinct vendors used by the filter dropdown. Loaded once — the reorder
  // rows can be narrower than the full item catalog so we source vendors from
  // the dedicated endpoint to guarantee every vendor appears even when nothing
  // for them is below PAR yet.
  useEffect(() => {
    api.get("/inventory/vendors").then((r) => {
      setVendorsList(r.data.vendors || []);
      setUnassignedCount(r.data.unassigned_count || 0);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cat) params.set("category", cat);
      if (vendor) params.set("vendor", vendor);
      const { data } = await api.get(`/inventory/reorder?${params.toString()}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [cat, vendor]);

  useEffect(() => { load(); }, [load]);

  const groupKeyFor = (r) => {
    if (groupBy === "vendor") return r.vendor || "Unassigned";
    if (groupBy === "location") return r.location_full || "—";
    return r.category || "Uncategorized";
  };

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const key = groupKeyFor(r);
      g[key] = g[key] || [];
      g[key].push(r);
    });
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, groupBy]);

  const categories = useMemo(() => Array.from(new Set(rows.map((r) => r.category).filter(Boolean))), [rows]);
  const totalCost = rows.reduce((s, r) => s + (r.estimated_cost || 0), 0);

  const exportUrl = (fmt) => {
    const p = new URLSearchParams({ fmt });
    if (vendor) p.set("vendor", vendor);
    if (cat) p.set("category", cat);
    return `/io/export/reorder?${p.toString()}`;
  };
  const filenameFor = (fmt) => {
    let vSlug = "all";
    if (vendor === UNASSIGNED) vSlug = "unassigned";
    else if (vendor) vSlug = vendor.replace(/[^a-zA-Z0-9]+/g, "_").slice(0, 40);
    return `reorder_${vSlug}.${fmt}`;
  };

  const vendorLabel = () => {
    if (vendor === "") return "All Vendors";
    if (vendor === UNASSIGNED) return "Unassigned";
    return vendor;
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label-tech mb-1">Purchasing</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Reorder List</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono" data-testid="reorder-summary">
            {rows.length} items below PAR · est. ${totalCost.toFixed(2)}
            <span className="ml-2 text-[10px] uppercase tracking-widest">· {vendorLabel()}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={IDS.exportReorder}
            onClick={() => downloadExport(exportUrl("xlsx"), filenameFor("xlsx"))}
            className="btn-hard flex items-center gap-1 h-10 px-3 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white"
          >
            <DownloadSimple size={14} weight="bold" /> Excel
          </button>
          <button
            data-testid="export-reorder-csv"
            onClick={() => downloadExport(exportUrl("csv"), filenameFor("csv"))}
            className="btn-hard flex items-center gap-1 h-10 px-3 border border-border text-foreground uppercase tracking-widest text-xs font-bold rounded-sm hover:bg-muted"
          >
            <DownloadSimple size={14} weight="bold" /> CSV
          </button>
        </div>
      </div>

      <div className="card-flat border-border p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs">
          <Funnel size={14} weight="bold" />
          <span className="label-tech">Group by</span>
        </div>
        {["category", "vendor", "location"].map((g) => (
          <button
            key={g}
            onClick={() => setGroupBy(g)}
            className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${
              groupBy === g ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
            }`}
          >{g}</button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <select
            data-testid="reorder-vendor-filter"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            className="h-8 px-2 border border-border bg-background rounded-sm text-xs min-w-[160px]"
          >
            <option value="">All Vendors</option>
            {vendorsList.map((v) => (
              <option key={v.vendor} value={v.vendor}>{v.vendor} ({v.count})</option>
            ))}
            {unassignedCount > 0 && (
              <option value={UNASSIGNED}>Unassigned ({unassignedCount})</option>
            )}
          </select>
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            className="h-8 px-2 border border-border bg-background rounded-sm text-xs"
          >
            <option value="">All categories</option>
            {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
      </div>

      {(() => {
        if (loading) return <div className="text-muted-foreground text-xs font-mono">Loading…</div>;
        if (rows.length === 0) {
          return (
            <div className="card-flat border-border p-12 text-center">
              <div className="font-display font-black text-xl mb-2">Everything is at PAR ✓</div>
              <div className="text-sm text-muted-foreground">No reorder recommendations {vendor ? `for ${vendorLabel()}` : "at this time"}.</div>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            {Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="card-flat border-border overflow-hidden">
                <div className="px-4 py-2 bg-muted/60 border-b border-border flex items-center justify-between">
                  <div className="font-display font-bold text-sm tracking-tight">{group}</div>
                  <div className="text-xs font-mono text-muted-foreground">
                    {items.length} items · ${items.reduce((s, r) => s + (r.estimated_cost || 0), 0).toFixed(2)}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border">
                      <tr className="text-left label-tech">
                        <th className="px-3 py-2">Product ID</th>
                        <th className="px-3 py-2">Product</th>
                        <th className="px-3 py-2 hidden md:table-cell">Vendor</th>
                        <th className="px-3 py-2 hidden md:table-cell">Location</th>
                        <th className="px-3 py-2 text-right">On Hand</th>
                        <th className="px-3 py-2 text-right">PAR</th>
                        <th className="px-3 py-2 text-right">Short</th>
                        <th className="px-3 py-2 text-right">Order Packs</th>
                        <th className="px-3 py-2 text-right">Order Cases</th>
                        <th className="px-3 py-2 text-right">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {items.map((r) => (
                        <tr key={`${r.variant_id}-${r.location_id}`} className="hover:bg-muted/30">
                          <td className="px-3 py-2 font-mono text-xs" data-testid={`reorder-bpid-${r.variant_id}`}>
                            {r.business_product_id && String(r.business_product_id).trim()
                              ? r.business_product_id
                              : <span className="text-muted-foreground italic">Missing</span>}
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium truncate max-w-[36ch]">{r.product_name}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">
                              {r.pack_size || "—"} · {r.units_per_pack} / pack
                              {r.pack_cost != null && (
                                <span className="ml-2">· pack ${Number(r.pack_cost).toFixed(2)}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 hidden md:table-cell text-xs">
                            {r.vendor && String(r.vendor).trim()
                              ? r.vendor
                              : <span className="text-muted-foreground italic">Unassigned</span>}
                          </td>
                          <td className="px-3 py-2 hidden md:table-cell text-xs">{r.location_full}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.current_qty}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.par_level}</td>
                          <td className="px-3 py-2 text-right font-mono text-destructive font-bold">{r.shortage}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.recommended_packs}</td>
                          <td className="px-3 py-2 text-right font-mono">{r.recommended_cases}</td>
                          <td className="px-3 py-2 text-right font-mono">${(r.estimated_cost || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}
